import React, { useEffect, useState } from 'react'
import './App.css'
import './KScannerPage.css'
import { Link } from 'react-router-dom'
import Header from './layouts/Header'

// ---- EDIT THESE ----
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.karamalescanner&hl=en' // direct APK link, or your Play Store URL
const APP_META = 'Free on Google Play · Android' // e.g. 'Android · APK · v1.0 · 48 MB'
// --------------------

const img = (name) => `${process.env.REACT_APP_API_URL}/apps/kscanner/karamale_scanner/${name}.webp`

const Phone = ({ name, alt, tilt }) => (
  <figure className={`kx-ks-phone ${tilt ? `kx-ks-phone--${tilt}` : ''}`}>
    <div className="kx-ks-phone__screen">
      <img src={img(name)} alt={alt} loading="lazy" />
    </div>
  </figure>
)

const DownloadButton = ({ variant = 'primary', children = 'Get it on Google Play' }) => (
  <a
    href={PLAY_STORE_URL}
    target="_blank"
    rel="noopener noreferrer"
    className={`kx-ks-btn kx-ks-btn--${variant}`}
  >
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M6 3.5v17l14-8.5z" fill="currentColor" />
    </svg>
    {children}
  </a>
)

const Eyebrow = ({ children, dark }) => (
  <div className={`kx-ks-eyebrow ${dark ? 'kx-ks-eyebrow--dark' : ''}`}>
    <span />
    {children}
  </div>
)

const Row = ({ reverse, eyebrow, title, accent, children, visual }) => (
  <section className={`kx-ks-row ${reverse ? 'kx-ks-row--reverse' : ''}`}>
    <div className="kx-ks-row__copy">
      <Eyebrow dark>{eyebrow}</Eyebrow>
      <h2>
        {title}
        <br />
        <strong>{accent}</strong>
      </h2>
      {children}
    </div>
    <div className="kx-ks-row__visual">{visual}</div>
  </section>
)

const Ticks = ({ items }) => (
  <ul className="kx-ks-ticks">
    {items.map((t) => (
      <li key={t}>
        <span>✓</span>
        <p>{t}</p>
      </li>
    ))}
  </ul>
)

const faqs = [
  {
    id: 'offline',
    question: 'Does Kscanner work without internet?',
    answer:
      'Yes. Local mode works offline on a single device. Live mode syncs in real time across multiple devices and needs a connection.',
  },
  {
    id: 'logout',
    question: 'What should I do before logging out?',
    answer:
      'Push your scans to the server first. Open the event, tap Push under Sync and wait for it to finish. Logging out before you push means unsynced scans are lost.',
  },
  {
    id: 'restore',
    question: 'How do I get my data back after logging in?',
    answer:
      'Log in, open the event and tap Pull under Sync. This downloads all scanned data to your device, including scans uploaded from other devices.',
  },
  {
    id: 'install',
    question: 'Where can I download Kscanner?',
    answer:
      'Kscanner is available on Google Play. Install it like any other app, then log in with your Karamale account.',
  },
]

const KScannerPage = () => {
  const [openFaq, setOpenFaq] = useState(null)

  useEffect(() => {
    document.title = 'Kscanner - Ticket scanning app by Karamale'
  }, [])

  return (
    <div className="kx-ks-page">
      <Header />

      {/* HERO */}
      <section className="kx-ks-hero">
        <div className="kx-ks-hero__grid" />
        <div className="kx-ks-hero__orb" />

        <div className="kx-ks-wrap kx-ks-hero__inner">
          <div className="kx-ks-hero__copy">
            <Eyebrow>KSCANNER BY KARAMALE</Eyebrow>

            <h1>
              Every ticket checked
              <br />
              <strong>in seconds.</strong>
            </h1>

            <p>
              The secure scanning app for your event entrance. Point, scan, and let Kscanner flag
              fake and invalid tickets instantly.
            </p>

            <div className="kx-ks-hero__actions">
              <DownloadButton />
              <a href="#screens" className="kx-ks-hero__link">
                See the app
                <span>↓</span>
              </a>
            </div>

            <small className="kx-ks-hero__meta">{APP_META}</small>
          </div>

          <div className="kx-ks-hero__visual">
            <div className="kx-ks-hero__phones">
              <Phone name="dashboard" alt="Kscanner check-in dashboard" tilt="left" />
              <Phone name="welcome" alt="Kscanner welcome screen" tilt="right" />
            </div>

            <div className="kx-ks-hero__mini">
              <span>COUNTERFEIT TICKETS</span>
              <strong>FLAGGED</strong>
              <small>instantly, at the gate</small>
            </div>

            <div className="kx-ks-hero__mini kx-ks-hero__mini--bottom">
              <span>SCAN MODES</span>
              <strong>LOCAL + LIVE</strong>
              <small>offline or real-time</small>
            </div>
          </div>
        </div>
      </section>

      {/* THREE PRINCIPLES */}
      <section className="kx-ks-principles">
        <div className="kx-ks-wrap">
          <div className="kx-ks-principles__intro">
            <span>THE KSCANNER WAY</span>
            <h2>
              Three things
              <br />
              to remember.
            </h2>
          </div>

          <div className="kx-ks-principles__items">
            <div>
              <span>01</span>
              <strong>Scan fast</strong>
              <p>Point your camera at a ticket. No typing, no hassle, even at busy entrances.</p>
            </div>
            <div>
              <span>02</span>
              <strong>Spot fakes</strong>
              <p>Counterfeit and invalid tickets are flagged automatically.</p>
            </div>
            <div>
              <span>03</span>
              <strong>Stay in sync</strong>
              <p>Push and pull keeps every scanner on your team consistent.</p>
            </div>
          </div>
        </div>
      </section>

      <main className="kx-ks-main">
        <div className="kx-ks-wrap">
          {/* SCREENS STRIP */}
          <section id="screens" className="kx-ks-strip">
            <div className="kx-ks-strip__heading">
              <Eyebrow dark>FIRST LAUNCH</Eyebrow>
              <h2>
                From install to
                <br />
                <strong>first scan.</strong>
              </h2>
            </div>

            <div className="kx-ks-strip__track">
              <div className="kx-ks-strip__item">
                <Phone name="ob-scan" alt="Scan tickets in seconds" />
                <span>01</span>
                <strong>Scan tickets in seconds</strong>
              </div>
              <div className="kx-ks-strip__item">
                <Phone name="ob-fakes" alt="Spot fakes instantly" />
                <span>02</span>
                <strong>Spot fakes instantly</strong>
              </div>
              <div className="kx-ks-strip__item">
                <Phone name="ob-gates" alt="Built for busy gates" />
                <span>03</span>
                <strong>Built for busy gates</strong>
              </div>
              <div className="kx-ks-strip__item">
                <Phone name="login" alt="Kscanner login" />
                <span>04</span>
                <strong>Log in and go</strong>
              </div>
            </div>
          </section>

          {/* DASHBOARD */}
          <Row
            eyebrow="CHECK-IN"
            title="Know your door"
            accent="at a glance."
            visual={<Phone name="dashboard" alt="Check-in progress and scan modes" />}
          >
            <p>
              Live check-in progress, physical and digital ticket counts and total sold, all on one
              screen. Start scanning in one tap.
            </p>
            <Ticks
              items={[
                'Progress bar and scanned-vs-sold count',
                'Physical and digital tickets tracked separately',
                'Sync controls right on the event screen',
              ]}
            />
          </Row>

          {/* LOCAL VS LIVE */}
          <section className="kx-ks-modes">
            <div className="kx-ks-modes__heading">
              <Eyebrow>START SCANNING</Eyebrow>
              <h2>
                Local or live.
                <br />
                <strong>Your call.</strong>
              </h2>
            </div>
            <div className="kx-ks-modes__cards">
              <article>
                <span>LOCAL</span>
                <strong>Works offline</strong>
                <p>Use it on one device, with no connection needed at the gate.</p>
              </article>
              <article className="kx-ks-modes__card--accent">
                <span>LIVE</span>
                <strong>Real-time</strong>
                <p>Multiple devices scanning the same event, kept in step as you go.</p>
              </article>
            </div>
          </section>

          {/* EVENTS */}
          <Row
            reverse
            eyebrow="YOUR EVENTS"
            title="All your events,"
            accent="one login."
            visual={<Phone name="events" alt="Event list in Kscanner" />}
          >
            <p>
              Sign in with your Karamale account and your events are ready. Pick one and start
              checking people in.
            </p>
          </Row>

          {/* SYNC */}
          <Row
            eyebrow="SYNC"
            title="Push. Pull."
            accent="Stay coordinated."
            visual={
              <div className="kx-ks-duo">
                <Phone name="sync" alt="Global sync with Pull & Merge and Push" />
                <Phone name="sync-about" alt="About syncing explainer" />
              </div>
            }
          >
            <p>
              Push sends the tickets scanned on this device so the rest of your team can see them.
              Pull brings the latest scans and status changes back. Fewer duplicates, fewer invalid
              scans.
            </p>
            <Ticks
              items={[
                'Global Sync covers all your events',
                'Selective Sync targets just one event, lighter on data',
                'Clear confirmation of what was reconciled',
              ]}
            />
          </Row>

          {/* LOGOUT */}
          <Row
            reverse
            eyebrow="BUILT-IN SAFETY NET"
            title="It warns you before"
            accent="you lose a scan."
            visual={<Phone name="logout" alt="Log out warning with sync steps" />}
          >
            <p>
              Before logging out, Kscanner reminds you to push unsynced scans, then shows exactly
              how to get your data back when you log in again.
            </p>
          </Row>

          {/* FAQ */}
          <section className="kx-ks-faq">
            <div className="kx-ks-faq__heading">
              <Eyebrow dark>QUESTIONS</Eyebrow>
              <h2>
                Frequently asked
                <br />
                <strong>questions.</strong>
              </h2>
            </div>

            <div className="kx-ks-faq__list">
              {faqs.map((item) => (
                <div key={item.id}>
                  <button
                    type="button"
                    className={`kx-ks-faq__item ${openFaq === item.id ? 'kx-ks-faq__item--open' : ''}`}
                    onClick={() => setOpenFaq((c) => (c === item.id ? null : item.id))}
                  >
                    <span>{item.question}</span>
                    <b>{openFaq === item.id ? '−' : '+'}</b>
                  </button>
                  {openFaq === item.id && <div className="kx-ks-faq__answer">{item.answer}</div>}
                </div>
              ))}
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="kx-ks-final">
            <div className="kx-ks-final__orb" />
            <div className="kx-ks-final__copy">
              <Eyebrow>READY WHEN YOU ARE</Eyebrow>
              <h2>
                Your next gate
                <br />
                <strong>starts here.</strong>
              </h2>
              <p>Download Kscanner, log in with your Karamale account and start scanning.</p>
              <small>{APP_META}</small>
            </div>
            <div className="kx-ks-final__actions">
              <DownloadButton variant="white" />
              <Link to="/organiser-registration" className="kx-ks-final__secondary">
                Create organiser account
              </Link>
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}

export default KScannerPage
